import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { UnitOfMeasure } from '../../enums';
import { BaseEntity } from './base.entity';
import { ItemEntity } from './item.entity';

/**
 * PD-15484 — a reusable merchandiser setup.
 *
 * Standing up a merchandiser is highly repeatable: the same shelves, the same
 * products in them, the same sensor module wired to the same shelf, the same
 * depths and capacities, unit after unit. Doing that by hand each time is slow
 * and easy to get subtly wrong — a swapped module or a mistyped depth is
 * invisible until the readings look strange weeks later.
 *
 * A template holds that shape once. Applying it to a new unit leaves the
 * operator entering only what is genuinely unique: the name, the serial
 * number, and which physical sensor is attached.
 *
 * A template is COPIED when applied, never linked: editing a template must not
 * silently rewrite the calibration of every unit built from it.
 */
@Entity('merchandiser_template')
export class MerchandiserTemplateEntity extends BaseEntity {
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	@Column('varchar', { name: 'description', length: 500, nullable: true })
	public description!: string | null;

	/** Retired templates stay for the units already built from them. */
	@Column({ type: 'boolean', name: 'isActive', width: 1, default: 1 })
	public isActive!: boolean;

	/**
	 * A template describes one physical cooler model, so it carries the unit's
	 * manufacturer/model (catalog_value ids, same catalogs as the merchandiser
	 * itself). Applying the template stamps them onto the unit so the Details
	 * tab is filled in rather than left looking "not set".
	 */
	@Column('int', {
		name: 'fk_catalog_value__id_manufacturer',
		unsigned: true,
		nullable: true,
	})
	public manufacturerId!: number | null;

	@Column('int', {
		name: 'fk_catalog_value__id_model',
		unsigned: true,
		nullable: true,
	})
	public modelId!: number | null;

	@OneToMany(
		() => MerchandiserTemplateLocationEntity,
		(location) => location.fkTemplate,
	)
	public locations!: Array<MerchandiserTemplateLocationEntity>;
}

/**
 * One shelf/zone in a template: the bin itself, the product that belongs in
 * it, and the sensor modules that watch it.
 *
 * Modules are held as NUMBERS (1-4, matching the device's own module
 * numbering) rather than sensor ids, because a template describes a shape
 * that any device of that layout can take — the real sensor ids differ on
 * every unit and are resolved at apply time.
 */
@Index('merchandiser_template_location_template_idx', ['templateId'])
@Entity('merchandiser_template_location')
export class MerchandiserTemplateLocationEntity extends BaseEntity {
	@Column('int', {
		name: 'fk_merchandiser_template__id',
		unsigned: true,
	})
	public templateId!: number;

	/** Position in the template, so applied bins come out in a stable order. */
	@Column('int', { name: 'sortOrder', default: 0 })
	public sortOrder!: number;

	@Column('varchar', { name: 'name', length: 255, nullable: true })
	public name!: string | null;

	@Column('varchar', { name: 'code', length: 64, nullable: true })
	public code!: string | null;

	@Column('int', { name: 'capacity', nullable: true })
	public capacity!: number | null;

	@Column('varchar', {
		name: 'units',
		length: 32,
		nullable: true,
	})
	public units!: UnitOfMeasure | null;

	/** The product that belongs in this bin. */
	@Column('int', { name: 'fk_item__id', unsigned: true, nullable: true })
	public itemId!: number | null;

	/**
	 * Which modules on the attached device watch this bin (1-based device
	 * module numbers). Null/empty for a bin with no sensor — a template may
	 * mix monitored and unmonitored shelves. A LIST because the field units
	 * pair two (sometimes three) proximity modules over one door; each listed
	 * module receives the same calibration at apply time.
	 */
	@Column('json', { name: 'sensorModuleNumbers', nullable: true })
	public sensorModuleNumbers!: Array<number> | null;

	/** Calibration copied onto the sensor when the template is applied. */
	@Column('int', { name: 'zoneDepthMm', nullable: true })
	public zoneDepthMm!: number | null;

	@Column('int', { name: 'minDistanceMm', nullable: true })
	public minDistanceMm!: number | null;

	@Column('int', { name: 'warnAboveTempF', nullable: true })
	public warnAboveTempF!: number | null;

	@Column('int', { name: 'warnBelowTempF', nullable: true })
	public warnBelowTempF!: number | null;

	@ManyToOne(() => MerchandiserTemplateEntity, (template) => template.locations)
	@JoinColumn({ name: 'fk_merchandiser_template__id' })
	public fkTemplate!: MerchandiserTemplateEntity;

	@ManyToOne(() => ItemEntity)
	@JoinColumn({ name: 'fk_item__id' })
	public fkItem!: ItemEntity | null;
}
